"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  SearchPanel,
  globalSearchDestination,
  type SearchPanelResult
} from "../../molecules/search-panel/search-panel";
import { LINK_POSITION_GLOBAL_NAV, fireLinkClick } from "../../services/analytics";
import styles from "./nav-search.module.css";
import { MIN_QUERY_LENGTH } from "./use-search-suggestions";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The seam between GlobalNav's search button and SearchPanel: a zero-height
 * `popover` shell under `panelId` with the panel inside it, so the browser owns
 * opening, closing, Escape, light-dismiss, the top layer and focus return.
 * Suggestions are not fetched here — `results` comes in and `onQueryChange`
 * goes out, debounced.
 *
 * ⚠ THE PANEL MUST BE A DESCENDANT OF THE SHELL, NOT ITS SIBLING. Light
 * dismiss closes an `auto` popover on any pointerdown outside its subtree, so
 * beside a 0x0 shell the first click into the search field would close it.
 *
 * ⚠ IT MUST RENDER INSIDE `[data-dds-root]`. The shell's insets are spacing
 * tokens; outside that wrapper both var()s are invalid at computed-value time,
 * the shell shrinks to 0 and the panel anchors off-screen.
 */

/** How long typing settles before the query leaves this component. */
export const DEFAULT_DEBOUNCE_MS = 500;

/**
 * The reported name of the nav's search button (HIM-4776 AC1). Not its
 * accessible name, which stays "Search" — a screen reader must not hear "icon".
 */
export const SEARCH_TRIGGER_LINK_NAME = "search icon";

/* Safari < 17 has no `:popover-open`, and `matches()` throws SyntaxError on an
   unknown pseudo-class instead of returning false — unguarded, it takes the
   page down on iOS 16. No Popover API there means nothing is ever open. */
const isPopoverOpen = (host: HTMLElement) => {
  try {
    return host.matches(":popover-open");
  } catch {
    return false;
  }
};

/**
 * Guarded because jsdom ships no Popover API — `showPopover`, `hidePopover`
 * and `togglePopover` are all undefined there, so closing the panel would
 * throw in a consumer's test. Not a browser-support guard.
 */
const dismiss = (host: HTMLElement | null) => {
  if (typeof host?.hidePopover !== "function") {
    return;
  }
  /* hidePopover() throws InvalidStateError when the popover is not showing,
     and the async callers below can land after the UA has closed it. */
  if (isPopoverOpen(host)) {
    host.hidePopover();
  }
};

export interface NavSearchProps {
  /**
   * ⚠ Must be the same string handed to GlobalNav as `search.panelId` — it
   * names the popover the nav's button opens.
   */
  panelId: string;
  /** Where a submitted search goes, forwarded to SearchPanel. Required. */
  action: string;
  /**
   * Suggestions for the current query, already fetched and already ordered.
   * ⚠ `undefined` means "not answered yet" and `[]` means "nothing matched" —
   * SearchPanel tells those apart, so do not collapse them.
   */
  results?: SearchPanelResult[];
  /**
   * The settled query. Fires after `debounceMs` of quiet, not per keystroke. A
   * query shorter than `MIN_QUERY_LENGTH` fetches nothing, so it fires at once.
   */
  onQueryChange?: (query: string) => void;
  /** Set to 0 to report every keystroke — for a story or a local fixture. */
  debounceMs?: number;
  /**
   * The submitted query's field name, forwarded to SearchPanel, which defaults
   * it to `"q"`. Name it when the results route reads a different key.
   */
  name?: string;
  label?: string;
  closeLabel?: string;
  emptyMessage?: string;
  /** A request is in flight, forwarded to SearchPanel. The host owns the fetch. */
  loading?: boolean;
  /** The search could not be reached, forwarded to SearchPanel. */
  errorMessage?: string;
  /** The backend status behind `errorMessage`, forwarded to SearchPanel. */
  errorCode?: string;
  /**
   * Draws the magnifier inside the panel's field, forwarded to SearchPanel.
   * No default here on purpose: the panel already defaults it off, and whether
   * the nav's search shows the mark is the nav owner's call, not this seam's.
   */
  fieldIcon?: boolean;
}

function NavSearchImpl({
  panelId,
  action,
  results,
  onQueryChange,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  name,
  label,
  closeLabel,
  emptyMessage,
  loading,
  errorMessage,
  errorCode,
  fieldIcon
}: NavSearchProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  /* A report is waiting out the debounce. Until it lands, `results` answer the
     previous query, so the panel treats them as loading rather than let them
     be read or selected against what is now in the box. */
  const [pending, setPending] = useState(false);

  /* Latest-wins, so the async timer never reads a stale prop and the listener
     effect need not re-subscribe. Written in an effect rather than the render
     body: a render React discards must not mutate a committed ref. */
  const reportRef = useRef(onQueryChange);
  useEffect(() => {
    reportRef.current = onQueryChange;
  });
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  /* Deduped because one close arrives twice — SearchPanel's close() reports ""
     itself and the UA's toggle then reaches the flush below. The owner drives
     the fetch, so a repeat is a repeat abort or refetch, not a no-op. */
  const lastSentRef = useRef<string>();
  const report = useCallback((query: string) => {
    if (lastSentRef.current === query) {
      return;
    }
    lastSentRef.current = query;
    reportRef.current?.(query);
  }, []);

  /* A failed query has to be sendable again, or retyping it hits the dedupe
     above and the error never clears. */
  useEffect(() => {
    if (errorMessage) {
      lastSentRef.current = undefined;
    }
  }, [errorMessage]);

  /* The shell sits in the top layer, positioned against the viewport, so the
     bar's offset has to be measured rather than inherited. The handle comes
     from the INVOKER — the one element guaranteed to exist and to name us —
     and its `<header>` ancestor is the bar. */
  const barElement = useCallback(() => {
    const invoker = document.querySelector<HTMLElement>(
      `[popovertarget="${CSS.escape(panelId)}"]`
    );
    /* With no `<header>`, the invoker's own box is the best anchor left for a
       shell opened programmatically. */
    return invoker?.closest("header") ?? invoker;
  }, [panelId]);

  /* Anchor and edge are parameters because the per-frame caller below already
     holds both; without them each frame costs a document query, an ancestor
     walk and a second forced layout. */
  const place = useCallback(
    (anchor?: HTMLElement | null, edge?: number) => {
      const host = hostRef.current;
      const bar = anchor ?? barElement();
      if (!host || !bar) {
        return;
      }
      const bottom = edge ?? bar.getBoundingClientRect().bottom;
      host.style.setProperty("--nav-search-top", `${bottom}px`);
    },
    [barElement]
  );

  /* ⚠ Placed on `beforetoggle`, not `toggle`: beforetoggle runs before the
     popover is shown, so the first frame is already in the right place.
     Afterwards, the panel paints at the fallback offset and then jumps. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }

    const onBeforeToggle = (event: Event) => {
      if ((event as ToggleEvent).newState === "open") {
        place();
      }
    };
    const onToggle = (event: Event) => {
      const opened = (event as ToggleEvent).newState === "open";
      setOpen(opened);
      if (!opened) {
        /* Light dismiss, Escape and the invoker's own toggle-off never reach
           SearchPanel's close(), so the flush happens here too — otherwise a
           pending timer serves the last reader's suggestions to the next. */
        clearTimeout(timerRef.current);
        setPending(false);
        report("");
      }
    };

    host.addEventListener("beforetoggle", onBeforeToggle);
    host.addEventListener("toggle", onToggle);

    /* ⚠ Adopt the state the DOM is already in. `popovertarget` opens the shell
       with no JavaScript, and no `toggle` fires for an open that has already
       happened — without this it takes three clicks to see a panel. */
    if (isPopoverOpen(host)) {
      setOpen(true);
    }
    /* Placed on mount for that same window, and for a story or test that opens
       the shell programmatically. */
    place();

    return () => {
      host.removeEventListener("beforetoggle", onBeforeToggle);
      host.removeEventListener("toggle", onToggle);
    };
  }, [place, report]);

  /* Follow the bar while the panel is open and close once the bar has gone,
     behind an rAF gate because a rect read is a forced layout.

     ⚠ TWO SIGNALS, because the rect cannot see the default mode: on
     `hide-on-scroll` the bar leaves by a 200ms transform, so the rect the
     scroll event schedules still returns its old on-screen bottom and no
     further scroll arrives. `data-hidden` is already true at that instant.
     The rect check stays for `sticky="off"`, where the bar really scrolls. */
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const bar = barElement();
    let frame = 0;
    const read = () => {
      frame = 0;
      if (!bar) {
        /* No captured anchor, so fall back to a fresh lookup — the only way a
           bar that mounted after this effect is picked up. */
        place();
        return;
      }
      const { bottom } = bar.getBoundingClientRect();
      if (bottom <= 0) {
        dismiss(hostRef.current);
        return;
      }
      place(bar, bottom);
    };
    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(read);
      }
    };

    const observer =
      bar && typeof MutationObserver === "function" ?
        new MutationObserver(() => {
          if (bar.getAttribute("data-hidden") === "true") {
            dismiss(hostRef.current);
          }
        }) :
        null;
    observer?.observe(bar as Element, {
      attributes: true,
      attributeFilter: ["data-hidden"]
    });

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [open, barElement, place]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Generated by Copilot- Start - 2026-10-06 11:14:48
  /* AC1: the nav's search button is reported from here, not by the container,
     which would name it "Search" (GlobalNav leaves it untracked for that
     reason). Delegated from the document and matched per click, so a button
     that mounts after this seam, or is replaced, is still reported. Opening
     clicks only: the click lands before the popover toggles, so a shell
     already open means this click is closing it. */
  useEffect(() => {
    const selector = `[popovertarget="${CSS.escape(panelId)}"]`;
    const report = (event: MouseEvent) => {
      if (!(event.target as Element | null)?.closest?.(selector)) {
        return;
      }
      if (hostRef.current && isPopoverOpen(hostRef.current)) {
        return;
      }
      fireLinkClick({
        linkName: SEARCH_TRIGGER_LINK_NAME,
        linkPosition: LINK_POSITION_GLOBAL_NAV,
        linkDestination: globalSearchDestination()
      });
    };
    document.addEventListener("click", report);
    return () => document.removeEventListener("click", report);
  }, [panelId]);
  // Generated by Copilot- End - 2026-10-06 11:14:48

  const handleQueryChange = useCallback(
    (query: string) => {
      clearTimeout(timerRef.current);
      const term = query.trim();
      /* A cleared box is not typing, so it does not wait: SearchPanel calls
         this with "" on close so the owner drops the last reader's
         suggestions, and a debounce would land that clear too late. A query
         below the minimum fetches nothing, so it has nothing to wait for. */
      if (term.length < MIN_QUERY_LENGTH || debounceMs <= 0) {
        setPending(false);
        report(query);
        return;
      }
      /* Loading only when a fetch will follow — a term already sent keeps its
         rows rather than flashing the skeleton for nothing. */
      setPending(term !== lastSentRef.current?.trim());
      timerRef.current = setTimeout(() => {
        setPending(false);
        report(query);
      }, debounceMs);
    },
    [debounceMs, report]
  );

  /* Closing goes through the DOM, not through state: the popover is the source
     of truth and hidePopover() fires `toggle`, which is where state catches
     up. ⚠ Writing state instead skips the browser's focus return. */
  const handleClose = useCallback(() => {
    dismiss(hostRef.current);
  }, []);

  return (
    <div className={styles.host} id={panelId} popover="auto" ref={hostRef}>
      <SearchPanel
        action={action}
        closeLabel={closeLabel}
        emptyMessage={emptyMessage}
        errorCode={errorCode}
        errorMessage={pending ? undefined : errorMessage}
        fieldIcon={fieldIcon}
        id={`${panelId}-panel`}
        label={label}
        loading={loading || pending}
        name={name}
        onClose={handleClose}
        onQueryChange={handleQueryChange}
        open={open}
        results={results}
      />
    </div>
  );
}
export const NavSearch = withSafeRender(NavSearchImpl, "NavSearch");

export default NavSearch;
